// src/hooks/useChats.ts
import { useState, useEffect } from 'react';
import { message } from 'antd';
import { 
  chatsApi, 
  ChatSession, 
  ChatMessage,
  isDRFResponse,
  isCustomResponse 
} from '@/services/api/chats';

interface ChatsParams {
  limit?: number;
  offset?: number;
  requires_attention?: boolean;
  mode?: 'auto' | 'manual';
  search?: string;
}

export const useChats = () => {
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [currentChat, setCurrentChat] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    has_more: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Загрузка списка чатов
  const loadChats = async (params?: ChatsParams) => {
    setLoading(true);
    try {
      const response = await chatsApi.getChats(params);
      const apiData = response.data.data;
      
      // Проверяем структуру ответа с помощью type guards
      if (isDRFResponse(apiData)) {
        // Стандартный формат DRF: {count, next, previous, results}
        setChats(apiData.results || []);
        setPagination({
          total: apiData.count || 0,
          limit: params?.limit || 20,
          offset: params?.offset || 0,
          has_more: !!apiData.next,
        });
      } else if (isCustomResponse(apiData)) {
        // Кастомный формат: {chats, pagination}
        setChats(apiData.chats || []);
        setPagination(apiData.pagination || {
          total: 0,
          limit: 20,
          offset: 0,
          has_more: false,
        });
      } else if (Array.isArray(apiData)) {
        // Прямой массив
        setChats(apiData);
        setPagination({
          total: apiData.length,
          limit: params?.limit || 20,
          offset: params?.offset || 0,
          has_more: false,
        });
      } else {
        setChats([]);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки чатов';
      message.error(errorMsg);
      setChats([]);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка сообщений чата
  const loadChatMessages = async (chatId: number) => {
    setMessagesLoading(true);
    try {
      const response = await chatsApi.getChatMessages(chatId, 500);
      const messagesData = response.data.data;
      
      // Поддержка разных форматов ответа
      if (Array.isArray(messagesData)) {
        setMessages(messagesData);
      } else if (messagesData && 'results' in messagesData) {
        const results = messagesData.results || [];
        setMessages(results);
      } else {
        setMessages([]);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки сообщений';
      message.error(errorMsg);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Отправить сообщение
  const sendMessage = async (chatId: number, messageText: string) => {
    setActionLoading(true);
    try {
      const response = await chatsApi.sendMessage(chatId, messageText);
      message.success('Сообщение отправлено');

      // Добавляем новое сообщение в список
      const newMessage = response.data.data.message;
      setMessages(prev => [...prev, newMessage]);

      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка отправки сообщения';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Взять чат в работу
  const takeControl = async (chatId: number) => {
    setActionLoading(true);
    try {
      const response = await chatsApi.takeControl(chatId);
      message.success('Чат взят в работу');
      
      // Обновляем локальное состояние
      setChats((prev: ChatSession[]) => prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, mode: 'manual' as const }
          : chat
      ));
      
      if (currentChat?.id === chatId) {
        setCurrentChat(prev => prev ? { ...prev, mode: 'manual' as const } : null);
      }
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка взятия чата';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Вернуть чат боту
  const releaseControl = async (chatId: number) => {
    setActionLoading(true);
    try {
      const response = await chatsApi.releaseControl(chatId);
      message.success('Чат возвращён боту');
      
      // Обновляем локальное состояние
      setChats((prev: ChatSession[]) => prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, mode: 'auto' as const, assigned_to: null, assigned_to_username: null }
          : chat
      ));
      
      if (currentChat?.id === chatId) {
        setCurrentChat(null);
        setMessages([]);
      }
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка возврата чата';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Открыть чат
  const openChat = async (chat: ChatSession) => {
    setCurrentChat(chat);
    await loadChatMessages(chat.id);
  };

  // Закрыть чат
  const closeChat = () => {
    setCurrentChat(null);
    setMessages([]);
  };

  // Перезагрузка текущих данных
  const refresh = () => {
    loadChats({
      limit: pagination.limit,
      offset: pagination.offset,
    });
  };

  // Загрузка базовых данных при монтировании
  useEffect(() => {
    loadChats({ requires_attention: true });
  }, []);

  return {
    // Данные
    chats,
    currentChat,
    messages,
    pagination,
    
    // Состояние загрузки
    loading,
    messagesLoading,
    actionLoading,
    
    // Методы
    loadChats,
    loadChatMessages,
    sendMessage,
    takeControl,
    releaseControl,
    openChat,
    closeChat,
    refresh,
  };
};